import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, ShieldAlert, Cloud, ToggleLeft, UserX, Tag, Trash2, GitMerge, AlertTriangle, Wand2, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";

// Category id -> icon + i18n label key (labels come from the existing reconcile.* strings).
const CAT = {
  usersMissingInEntra: { icon: Cloud, label: "reconcile.usersMissingEntra", actionLabel: "reconcile.fixSync" },
  usersMissingInAd: { icon: ToggleLeft, label: "reconcile.usersMissingAd", actionLabel: "reconcile.fixDisable" },
  userEnabledMismatch: { icon: UserX, label: "reconcile.enabledMismatch", actionLabel: "reconcile.fixMatchAd" },
  userSamMismatch: { icon: Tag, label: "reconcile.samMismatch", actionLabel: "reconcile.fixResetSam" },
  groupsMissingInAd: { icon: Trash2, label: "reconcile.groupsMissingAd", actionLabel: "reconcile.fixDelete" },
  membershipDivergence: { icon: GitMerge, label: "reconcile.membershipDivergence", actionLabel: "reconcile.fixReconcileMembership" },
};
const RISK = {
  high: "text-rose-700 bg-rose-50 border-rose-200",
  medium: "text-amber-700 bg-amber-50 border-amber-200",
  low: "text-emerald-700 bg-emerald-50 border-emerald-200",
};

// AI-assisted drift remediation loop: turns the latest AD↔Entra drift into ranked, one-click,
// human-approved fixes right in the Action Log. Admin + AI-licensed only; renders nothing otherwise.
export function AiRemediationPanel() {
  const { t, lang } = useLang();
  const { isAdmin, ai } = useAuth();
  const aiOn = !!(ai?.available && ai?.canUse);
  const [drift, setDrift] = useState(null);       // {total, status} from /reconcile/report
  const [plan, setPlan] = useState(null);         // AI plan from /ai/remediation-plan
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState("");   // category id currently applying
  const [rescanning, setRescanning] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const loadDrift = async () => {
    try {
      const { data } = await api.get("/reconcile/report");
      const rep = data?.report;
      setDrift(rep ? { total: rep.summary?.totalMismatches ?? 0, status: rep.status, ranAt: rep.ranAt } : null);
    } catch { setDrift(null); }
  };
  const rescan = async () => {
    setRescanning(true);
    try { await api.post("/reconcile/run"); setPlan(null); await loadDrift(); }
    catch (e) { toast.error(apiErr(e)); } finally { setRescanning(false); }
  };
  useEffect(() => { if (isAdmin && aiOn) loadDrift(); }, [isAdmin, aiOn]);

  if (!isAdmin || !aiOn) return null;
  const hasDrift = drift && drift.total > 0 && drift.status !== "noInventory";
  if (!hasDrift) return null;

  const analyze = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/remediation-plan", { language: lang });
      setPlan(data);
      if (!data.suggestions?.length) toast.info(t("airem.noActionable"));
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const applyKeys = async (s, confirm) => {
    setApplying(s.id);
    const tId = toast.loading(t("airem.applying").replace("{n}", s.keys.length));
    let ok = 0; let lastErr = "";
    for (const key of s.keys) {
      try { await api.post("/reconcile/remediate", { action: s.action, key, confirm, rerun: false }); ok++; }
      catch (e) { lastErr = apiErr(e); }
    }
    try { await api.post("/reconcile/run"); } catch { /* */ }
    if (ok === 0) toast.error(lastErr || t("airem.applyFailed"), { id: tId });
    else if (ok < s.keys.length) toast.warning(t("airem.appliedPartial").replace("{ok}", ok).replace("{total}", s.keys.length), { id: tId });
    else toast.success(t("airem.applied").replace("{ok}", ok).replace("{total}", s.keys.length), { id: tId });
    setApplying(""); setConfirmState(null); setConfirmText("");
    // Never auto re-run the LLM (cost + intent): drop the stale plan and refresh only the drift badge.
    setPlan(null);
    await loadDrift();
  };

  // Every apply mutates the LIVE tenant in bulk, so ALWAYS require a human confirm step.
  // Destructive actions (delete/rename) also require typing the exact token.
  const onApply = (s) => { setConfirmState(s); setConfirmText(""); };

  return (
    <div className="mb-5 border border-primary/30 bg-primary/[0.03]" data-testid="ai-remediation-panel">
      <div className="flex items-center justify-between px-4 py-3">
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid="ai-remediation-toggle"
          className="flex items-center gap-2 text-left">
          {open ? <ChevronDown size={16} className="text-primary" /> : <ChevronRight size={16} className="text-primary" />}
          <Sparkles size={16} className="text-primary" />
          <span className="text-sm font-bold text-zinc-900">{t("airem.title")}</span>
          <span className="rounded-sm border border-amber-200 bg-amber-50 px-2 py-0.5 font-mono text-[11px] font-bold text-amber-700">{drift.total}</span>
        </button>
        {!plan && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={rescan} disabled={rescanning || loading} data-testid="ai-remediation-rescan"
              className="h-8 rounded-sm border-zinc-300">
              <RefreshCw size={13} className={`mr-1.5 ${rescanning ? "animate-spin" : ""}`} /> {t("airem.rescan")}
            </Button>
            <Button onClick={analyze} disabled={loading || rescanning} data-testid="ai-remediation-analyze"
              className="h-8 rounded-sm bg-primary text-primary-foreground hover:bg-[#002480]">
              <Wand2 size={14} className={`mr-1.5 ${loading ? "animate-pulse" : ""}`} /> {loading ? t("airem.analyzing") : t("airem.analyze")}
            </Button>
          </div>
        )}
      </div>

      {open && (
        <div className="border-t border-primary/20 px-4 py-3">
          {drift.ranAt && <p className="mb-2 font-mono text-[11px] text-zinc-400" data-testid="ai-remediation-reportat">{t("airem.reportAt").replace("{d}", new Date(drift.ranAt).toLocaleString())}</p>}
          {!plan && <p className="text-sm text-zinc-500">{t("airem.intro")}</p>}
          {plan && (
            <div className="space-y-3">
              {plan.overview && (
                <p className="rounded-sm border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700" data-testid="ai-remediation-overview">{plan.overview}</p>
              )}
              {(plan.suggestions || []).length === 0 && (
                <p className="text-sm text-zinc-500" data-testid="ai-remediation-empty">{t("airem.noActionable")}</p>
              )}
              {(plan.suggestions || []).map((s) => {
                const meta = CAT[s.id] || {};
                const Icon = meta.icon || ShieldAlert;
                return (
                  <div key={s.id} className="border border-zinc-200 bg-white p-3" data-testid={`ai-remediation-suggestion-${s.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="flex min-w-0 items-start gap-2">
                        <Icon size={16} className="mt-0.5 shrink-0 text-primary" />
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-bold text-zinc-900">{t(meta.label) || s.id}</span>
                            <span className="rounded-sm border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-[10px] text-zinc-600">{s.count}</span>
                            {s.risk && <span className={`rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase ${RISK[s.risk] || ""}`} data-testid={`ai-remediation-risk-${s.id}`}>{t(`airem.risk.${s.risk}`)}</span>}
                            {s.priority != null && <span className="rounded-sm border border-zinc-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-zinc-500">#{s.priority}</span>}
                          </div>
                          {s.rationale && <p className="mt-1 text-sm text-zinc-600" data-testid={`ai-remediation-rationale-${s.id}`}>{s.rationale}</p>}
                        </div>
                      </div>
                      <Button onClick={() => onApply(s)} disabled={!!applying}
                        data-testid={`ai-remediation-apply-${s.id}`}
                        className={`h-8 shrink-0 rounded-sm ${s.destructive ? "bg-red-600 hover:bg-red-700" : "bg-primary hover:bg-[#002480]"} text-primary-foreground`}>
                        {s.destructive && <AlertTriangle size={13} className="mr-1.5" />}
                        {applying === s.id ? t("airem.applyingShort") : `${t(meta.actionLabel) || t("airem.apply")} (${s.count})`}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!confirmState} onOpenChange={(o) => { if (!o) { setConfirmState(null); setConfirmText(""); } }}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="ai-remediation-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{confirmState?.confirmToken ? t("airem.confirmTitle") : t("airem.confirmTitlePlain")}</DialogTitle>
            <DialogDescription>
              {confirmState?.confirmToken
                ? t("airem.confirmBody").replace("{n}", confirmState?.count ?? "").replace("{token}", confirmState?.confirmToken || "")
                : t("airem.confirmBodyPlain").replace("{n}", confirmState?.count ?? "").replace("{action}", confirmState ? (t(CAT[confirmState.id]?.actionLabel) || confirmState.action) : "")}
            </DialogDescription>
          </DialogHeader>
          {confirmState?.destructive && (
            <p className="flex items-start gap-1.5 rounded-sm border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-700"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> {t("airem.confirmWarn")}</p>
          )}
          {confirmState?.confirmToken && (
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={confirmState?.confirmToken}
              data-testid="ai-remediation-confirm-input" className="rounded-sm border-zinc-300 font-mono" />
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setConfirmState(null); setConfirmText(""); }} data-testid="ai-remediation-confirm-cancel" className="rounded-sm border-zinc-300">{t("jobs.cancel")}</Button>
            <Button onClick={() => applyKeys(confirmState, confirmState.confirmToken || undefined)}
              disabled={(confirmState?.confirmToken && confirmText.trim().toUpperCase() !== confirmState.confirmToken) || !!applying}
              data-testid="ai-remediation-confirm-apply" className={`rounded-sm ${confirmState?.destructive ? "bg-red-600 hover:bg-red-700" : "bg-primary hover:bg-[#002480]"} text-primary-foreground`}>
              {confirmState?.destructive && <Trash2 size={14} className="mr-1.5" />} {t("airem.proceed")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
